// ============================================================
// frontend/src/views/TemplatesView.tsx
// ============================================================
// Package templates CRUD. Archive (soft delete) + restore.
// ============================================================

import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useSaving } from '../lib/useSaving';
import { Modal, ModalFooter, Field } from '../components/UI';
import { SaveButton } from '../components/SaveButton';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type {
  PackageTemplate, TemplateListResponse, TemplateResponse,
  CreateTemplateRequest, Settings,
} from '@consilium/shared';

interface TemplatesViewProps {
  showError: (msg: string) => void;
  settings: Settings | null;
}

export function TemplatesView({ showError, settings }: TemplatesViewProps) {
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PackageTemplate | null>(null);

  // Track which template's archive button is "saving" — per-row state
  // means the rest of the page stays interactive.
  const [archivingId, setArchivingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { templates } = await api.get<TemplateListResponse>('/templates');
        if (!cancelled) setTemplates(templates);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const active = templates.filter((t) => !t.archived);
  const archived = templates.filter((t) => t.archived);

  // Local upsert helper — used on create AND edit
  const upsertTemplate = (t: PackageTemplate) => {
    setTemplates((prev) => {
      const idx = prev.findIndex((x) => x.id === t.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = t;
        return next;
      }
      return [t, ...prev];
    });
  };

  const archiveTemplate = async (t: PackageTemplate) => {
    if (!confirm('Archive this template? Clients with assigned packages keep their copies.')) return;
    setArchivingId(t.id);
    try {
      await api.del(`/templates/${t.id}`);
      setTemplates((prev) => prev.map((x) => x.id === t.id ? { ...x, archived: true } : x));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to archive');
    } finally {
      setArchivingId(null);
    }
  };

  const restoreTemplate = async (t: PackageTemplate) => {
    try {
      await api.post(`/templates/${t.id}/restore`);
      setTemplates((prev) => prev.map((x) => x.id === t.id ? { ...x, archived: false } : x));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to restore');
    }
  };

  return (
    <div className="fade-in">
      <div className="row" style={{ alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div className="mono-cap">Catalogue</div>
          <h1 className="page-title">Package templates</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, maxWidth: 600 }}>
            Reusable blueprints. Editing a template doesn't affect packages already assigned to clients.
          </div>
        </div>
        <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={16} />New template
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><ConsiliumLoader size={48} /></div>
      ) : active.length === 0 ? (
        <div className="card empty-state">
          <div className="display">No templates yet</div>
          <div style={{ fontSize: 14 }}>Create templates for the packages you commonly offer.</div>
        </div>
      ) : (
        <div className="stack">
          {active.map((t) => (
            <div key={t.id} className="card">
              <div className="row" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="display" style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em' }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <span><strong style={{ color: 'var(--ink)' }}>{t.totalSessions}</strong> sessions</span>
                    <span><strong style={{ color: 'var(--ink)' }}>{t.totalFee.toLocaleString()}</strong> {t.currency}</span>
                    <span style={{ color: 'var(--muted-strong)' }}>
                      {Math.round(t.totalFee / t.totalSessions).toLocaleString()}/session
                    </span>
                  </div>
                  {t.notes && (
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
                      {t.notes}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn-icon" onClick={() => { setEditing(t); setShowForm(true); }}>
                    <Edit2 size={15} />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => archiveTemplate(t)}
                    disabled={archivingId === t.id}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <details style={{ marginTop: 24 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--muted)', padding: 8 }}>
            {archived.length} archived template{archived.length > 1 ? 's' : ''}
          </summary>
          <div className="stack" style={{ marginTop: 8 }}>
            {archived.map((t) => (
              <div key={t.id} className="card" style={{ opacity: 0.6 }}>
                <div className="row">
                  <div>
                    <div style={{ fontSize: 14 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {t.totalSessions} sessions · {t.totalFee.toLocaleString()} {t.currency}
                    </div>
                  </div>
                  <button
                    className="btn-ghost"
                    style={{ padding: '6px 14px', fontSize: 12, minHeight: 32 }}
                    onClick={() => restoreTemplate(t)}
                  >
                    Restore
                  </button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {showForm && (
        <TemplateForm
          initial={editing}
          defaultCurrency={settings?.defaultCurrency}
          onSaved={(t) => { upsertTemplate(t); setShowForm(false); }}
          onCancel={() => setShowForm(false)}
          showError={showError}
        />
      )}
    </div>
  );
}

// ============================================================
// TemplateForm
// ============================================================
interface TemplateFormProps {
  initial: PackageTemplate | null;
  defaultCurrency?: string;
  onSaved: (t: PackageTemplate) => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}

function TemplateForm({ initial, defaultCurrency, onSaved, onCancel, showError }: TemplateFormProps) {
  const isEdit = !!initial;
  const [data, setData] = useState<CreateTemplateRequest>(initial
    ? {
        name: initial.name,
        totalSessions: initial.totalSessions,
        totalFee: initial.totalFee,
        currency: initial.currency,
        notes: initial.notes,
      }
    : {
        name: '', totalSessions: 8, totalFee: 0,
        currency: defaultCurrency || 'PKR', notes: '',
      }
  );
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!data.name.trim() || !data.totalFee) return;
    try {
      const result = await run(async () => {
        const payload: CreateTemplateRequest = {
          name: data.name,
          totalSessions: Number(data.totalSessions),
          totalFee: Number(data.totalFee),
          currency: data.currency,
          notes: data.notes,
        };
        const r = isEdit
          ? await api.put<TemplateResponse>(`/templates/${initial!.id}`, payload)
          : await api.post<TemplateResponse>('/templates', payload);
        return r.template;
      });
      if (!result) return;
      setTimeout(() => onSaved(result), 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <Modal title={isEdit ? 'Edit template' : 'New package template'} onClose={onCancel}>
      <div className="stack-lg">
        <Field label="Template name" required>
          <input
            className="input" autoFocus
            placeholder="e.g. 8-session CBT package"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
          />
        </Field>
        <div className="grid-3">
          <Field label="Sessions" required>
            <input
              type="number" min={1} className="input"
              value={data.totalSessions}
              onChange={(e) => setData({ ...data, totalSessions: Number(e.target.value) })}
            />
          </Field>
          <Field label="Total fee" required>
            <input
              type="number" min={0} className="input"
              value={data.totalFee || ''}
              onChange={(e) => setData({ ...data, totalFee: Number(e.target.value) })}
            />
          </Field>
          <Field label="Currency">
            <select
              className="input" value={data.currency}
              onChange={(e) => setData({ ...data, currency: e.target.value })}
            >
              <option value="PKR">PKR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </Field>
        </div>
        <Field label="Default notes" hint="Copied to each assigned package; can be edited per-client.">
          <textarea
            className="input" value={data.notes || ''}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            placeholder="What this package is for, typical structure, etc."
          />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={!data.name.trim() || !data.totalFee}
        >
          {isEdit ? 'Save changes' : 'Create template'}
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}
